import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const PRODUCT = '15457901576324';
const memories = new Map();
const choices = new Map();
const sectionKey = node => node.closest('.shopify-section')?.id || PRODUCT;
const emptyChoice = color => ({ color: color || 'Light Skin', band: '', cup: '', touched: false });
const lookup = (map, choice) => map?.[`${choice.color}|${choice.band}|${choice.cup}`] || null;
const mainFor = node => node.closest('.shopify-section')?.querySelector('afz-staylift-picker');
const bundleFor = node => node.closest('.shopify-section')?.querySelector('afz-staylift-bundle');

class StayLiftPicker extends HTMLElement {
  connectedCallback() {
    if (this.bound || this.dataset.productId !== PRODUCT) return;
    this.native = this.querySelector('variant-picker');
    const data = this.querySelector('[data-staylift-fit-map]');
    if (!this.native || !data) return;
    this.map = JSON.parse(data.textContent);
    this.choice = choices.get(sectionKey(this)) || this.defaultChoice();
    this.selectionVersion ||= 0;
    choices.set(sectionKey(this), this.choice);
    this.bound = true;
    this.controller = new AbortController();
    this.addEventListener('click', this.onClick, { signal: this.controller.signal });
    this.addEventListener('change', this.onNativeChange, { capture: true, signal: this.controller.signal });
    this.observer = new MutationObserver(() => this.paint());
    this.observer.observe(this.native, { childList: true, subtree: true });
    this.paint();
    bundleFor(this)?.syncMain?.();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.bound = false;
  }

  nativeColor() {
    return this.native?.querySelector('input[data-option-name="Color"]:checked')?.value || 'Light Skin';
  }

  defaultChoice() {
    const entries = Object.entries(this.map);
    const requestedId = new URL(window.location.href).searchParams.get('variant');
    const selectedIds = new Set([...this.native.querySelectorAll('input[data-option-value-id]:checked')].map(input => input.dataset.optionValueId));
    const selected = entries.find(([, fit]) => fit.id === requestedId)
      || entries.find(([, fit]) => fit.ids.every(id => selectedIds.has(id)))
      || entries[0];
    if (!selected) return emptyChoice(this.nativeColor());
    const [color, band, cup] = selected[0].split('|');
    return { color, band, cup, touched: false };
  }

  onNativeChange = event => {
    const name = event.target.dataset.optionName;
    if (!['Color', 'Size'].includes(name)) return;
    if (!this.applyingNative) this.selectionVersion++;
    this.nativeEventContext = { version: this.selectionVersion, origin: this.applyingNative ? 'internal' : name === 'Size' ? 'external-size' : 'color' };
    if (name === 'Color') {
      this.choice.color = event.target.value;
      this.choice.touched = true;
      const fit = lookup(this.map, this.choice);
      if (fit) event.target.dataset.variantId = fit.id;
      this.paint();
    }
  };

  onClick = event => {
    const guide = event.target.closest('[data-staylift-size-guide]');
    if (guide) {
      const details = this.closest('.shopify-section')?.querySelector('[data-afz-size-guide]');
      if (details) { details.open = true; details.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
      return;
    }
    const button = event.target.closest('[data-staylift-band], [data-staylift-cup]');
    if (!button || button.disabled) return;
    if (button.dataset.stayliftBand) this.choice.band = button.dataset.stayliftBand;
    if (button.dataset.stayliftCup) this.choice.cup = button.dataset.stayliftCup;
    if (this.choice.band === '40' && ['A', 'B', 'C'].includes(this.choice.cup)) this.choice.cup = '';
    this.choice.touched = true;
    this.commit();
  };

  setChoice(value) {
    Object.assign(this.choice, value, { touched: true });
    if (this.choice.band === '40' && ['A', 'B', 'C'].includes(this.choice.cup)) this.choice.cup = '';
    this.commit();
  }

  commit() {
    this.selectionVersion++;
    this.paint();
    const fit = lookup(this.map, this.choice);
    if (!fit) {
      const color = [...this.native.querySelectorAll('input[data-option-name="Color"]')].find(input => input.value === this.choice.color);
      if (color && !color.checked) {
        this.applyingNative = true;
        color.checked = true; color.dispatchEvent(new Event('change', { bubbles: true }));
        this.applyingNative = false;
      }
      else bundleFor(this)?.syncMain?.();
      return;
    }
    const color = this.native.querySelector(`input[data-option-value-id="${fit.ids[0]}"]`);
    const size = this.native.querySelector(`input[data-option-value-id="${fit.ids[1]}"]`);
    if (!color || !size) return;
    if (typeof this.native.updateSelectedOption !== 'function') {
      customElements.whenDefined('variant-picker').then(() => { if (this.isConnected) this.commit(); });
      return;
    }
    const changed = !color.checked || !size.checked;
    if (changed) {
      this.native.updateSelectedOption(color);
      size.dataset.variantId = fit.id;
      size.checked = true;
      this.applyingNative = true;
      size.dispatchEvent(new Event('change', { bubbles: true }));
      this.applyingNative = false;
    } else bundleFor(this)?.syncMain?.();
  }

  reconcileNative(id, context) {
    if (context.version !== this.selectionVersion) return;
    const actual = Object.entries(this.map).find(([, value]) => value.id === String(id));
    if (!actual) return;
    const [color, band] = actual[0].split('|');
    this.choice.color = color;
    if (context.origin !== 'external-size' && !lookup(this.map, this.choice)) { this.paint(); return; }
    if (this.choice.band || context.origin === 'external-size') this.choice.band = band;
    const compatible = lookup(this.map, this.choice);
    if (this.choice.cup && (!compatible || compatible.id !== String(id))) this.choice.cup = '';
    this.paint();
  }

  paint() {
    this.native.querySelectorAll('input[data-option-name="Size"]').forEach(input => input.closest('fieldset')?.classList.add('afz-staylift-native-size'));
    const labels = [['band', 'Choose band'], ['cup', 'Choose cup']];
    for (const [key, placeholder] of labels) {
      const node = this.querySelector(`[data-staylift-${key}-value]`);
      const value = this.choice[key] || placeholder;
      if (node && node.textContent !== value) node.textContent = value;
    }
    this.querySelectorAll('[data-staylift-band]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.stayliftBand === this.choice.band)));
    this.querySelectorAll('[data-staylift-cup]').forEach(button => {
      const cup = button.dataset.stayliftCup;
      button.disabled = this.choice.band === '40' && ['A', 'B', 'C'].includes(cup);
      button.setAttribute('aria-pressed', String(cup === this.choice.cup));
    });
    const complete = Boolean(lookup(this.map, this.choice));
    this.dataset.fitComplete = String(complete);
    this.dataset.ready = 'true';
    const message = this.querySelector('[data-staylift-fit-message]');
    if (message) {
      const text = complete ? '' : this.choice.band === '40' ? 'Choose cup D, DD or E for band 40.' : 'Choose your band and cup size.';
      if (message.textContent !== text) message.textContent = text;
    }
    const bundle = bundleFor(this);
    if (bundle?.picks) bundle.updateControls();
  }
}

if (!customElements.get('afz-staylift-picker')) customElements.define('afz-staylift-picker', StayLiftPicker);

await customElements.whenDefined('afz-bundle-selector');
const NativeBundle = customElements.get('afz-bundle-selector');

class StayLiftBundle extends NativeBundle {
  connectedCallback() {
    if (this.dataset.productId !== PRODUCT || this.controller) return;
    this.fitChoices = memories.get(sectionKey(this)) || Array.from({ length: 6 }, () => emptyChoice());
    memories.set(sectionKey(this), this.fitChoices);
    this.nativeSelectHandler ||= this.onNativeSelect;
    this.onNativeSelect = async event => {
      const generation = (this.fitNativeGeneration || 0) + 1;
      this.fitNativeGeneration = generation;
      const picker = this.picker();
      const context = { ...(picker?.nativeEventContext || { version: picker?.selectionVersion, origin: 'external-size' }) };
      await this.nativeSelectHandler(event);
      if (this.isConnected && generation === this.fitNativeGeneration) {
        if (!this.picks[0]?.error) picker?.reconcileNative(this.seed?.variant?.id, context);
        this.syncMain();
      }
    };
    this.onSubmit = event => this.submitFits(event);
    super.connectedCallback();
    this.syncMain();
  }

  picker() { return mainFor(this); }
  fitAt(index) { return lookup(this.picker()?.map, this.fitChoices?.[index]); }

  syncMain() {
    if (!this.picks || !this.isConnected) return;
    const picker = this.picker();
    if (!picker?.choice) return;
    this.fitChoices[0] = { ...picker.choice, touched: true };
    const fit = lookup(picker.map, picker.choice);
    if (fit) {
      for (let index = 1; index < this.fitChoices.length; index++) {
        if (!this.fitChoices[index].touched) this.fitChoices[index] = { ...picker.choice, touched: false };
      }
    }
    this.fitChoices.forEach((choice, index) => {
      const target = lookup(picker.map, choice);
      const pick = this.picks[index];
      if (!target || !pick || String(pick.state.variant?.id) === target.id || pick.pending) return;
      if (String(this.seed?.variant?.id) === target.id) this.picks[index] = this.makePick(this.seed);
      else {
        pick.ids = target.ids.slice();
        this.resolve(index);
      }
    });
    this.render();
  }

  canSubmit() {
    if (!this.fitChoices || !super.canSubmit()) return false;
    return this.picks.slice(0, this.quantity).every((pick, index) => {
      const fit = this.fitAt(index);
      return fit && fit.id === String(pick.state.variant?.id);
    });
  }

  updateControls() {
    super.updateControls();
    const status = this.querySelector('[data-status]');
    const prompt = 'Choose a band and cup size for each item.';
    const active = this.picks.slice(0, this.quantity);
    if (this.busy || this.nativePending || active.some(pick => pick.pending || pick.error || !pick.state.variant?.available)) return;
    if (super.canSubmit() && active.some((pick, index) => !this.fitAt(index))) status.textContent = prompt;
    else if (status.textContent === prompt) status.textContent = '';
  }

  async resolve(index) {
    await super.resolve(index);
    if (!this.isConnected) return;
    const pick = this.picks[index];
    const target = this.fitAt(index);
    if (!pick || !target || pick.pending || pick.error || String(pick.state.variant?.id) === target.id) return;
    if (String(this.seed?.variant?.id) === target.id) {
      this.picks[index] = this.makePick(this.seed);
      this.render();
    } else if (pick.ids.join(',') !== target.ids.join(',')) {
      pick.ids = target.ids.slice();
      return this.resolve(index);
    } else {
      pick.error = this.strings.retry;
      this.render();
    }
  }

  render() {
    const active = this.contains(document.activeElement) ? document.activeElement : null;
    const index = active?.closest('[data-item-index]')?.dataset.itemIndex;
    const key = active?.dataset.stayliftField || (active?.dataset.optionIndex === '0' ? 'color' : '');
    super.render();
    if (index !== undefined && key) {
      const selector = key === 'color' ? 'select[data-option-index="0"]' : `select[data-staylift-field="${key}"]`;
      this.querySelector(`.is-selected [data-item-index="${index}"] ${selector}`)?.focus({ preventScroll: true });
    }
  }

  createRow(pick, index) {
    const row = super.createRow(pick, index);
    const fields = row.querySelector('.afz-bundle__fields');
    const color = row.querySelector('select[data-option-index="0"]');
    const nativeSize = row.querySelector('select[data-option-index="1"]');
    nativeSize?.closest('label')?.classList.add('afz-staylift-native-size');
    if (!this.fitChoices[index]) this.fitChoices[index] = emptyChoice();
    const choice = this.fitChoices[index];
    const colorText = color?.selectedOptions[0]?.textContent;
    if (!choice.touched && !choice.band && colorText) choice.color = colorText;
    fields.style.setProperty('--bundle-options', '3');
    for (const [key, values, label] of [['band', ['32', '34', '36', '38', '40'], 'Band'], ['cup', ['A', 'B', 'C', 'D', 'DD', 'E'], 'Cup']]) {
      const field = document.createElement('label');
      field.className = 'afz-bundle__field';
      const title = document.createElement('span'); title.textContent = label;
      const select = document.createElement('select');
      select.dataset.stayliftField = key;
      select.setAttribute('aria-label', `Item ${index + 1} · ${label}`);
      select.append(new Option(`Choose ${label.toLowerCase()}`, ''));
      values.forEach(value => {
        const option = new Option(value, value);
        option.disabled = key === 'cup' && choice.band === '40' && ['A', 'B', 'C'].includes(value);
        select.append(option);
      });
      select.value = choice[key];
      field.append(title, select); fields.append(field);
    }
    row.addEventListener('change', event => {
      const target = event.target;
      const key = target.dataset.stayliftField;
      const isColor = target.matches('select[data-option-index="0"]');
      if (!key && !isColor) return;
      if (isColor) choice.color = target.selectedOptions[0].textContent;
      else choice[key] = target.value;
      choice.touched = true;
      if (choice.band === '40' && ['A', 'B', 'C'].includes(choice.cup)) choice.cup = '';
      if (index === 0) {
        event.stopPropagation();
        this.picker()?.setChoice(choice);
        return;
      }
      if (isColor) return;
      const fit = this.fitAt(index);
      const option = fit && [...nativeSize.options].find(value => value.value === fit.ids[1]);
      if (option && nativeSize.value !== option.value) {
        nativeSize.value = option.value;
        nativeSize.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.render();
    });
    return row;
  }

  syncAcceleratedCheckout(ready) {
    super.syncAcceleratedCheckout(ready);
    if (!this.form) return;
    this.form.querySelectorAll('[data-staylift-fit-property]').forEach(input => input.remove());
    if (this.quantity !== 1 || !this.fitAt(0)) return;
    for (const key of ['Band', 'Cup']) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = `properties[${key}]`;
      input.value = this.fitChoices[0][key.toLowerCase()];
      input.dataset.stayliftFitProperty = 'true';
      this.form.append(input);
    }
  }

  fitCartLines(quantity) {
    const lines = new Map();
    for (let index = 0; index < quantity; index++) {
      const variant = this.picks[index].state.variant;
      const choice = this.fitChoices[index];
      const key = `${variant.id}|${choice.band}|${choice.cup}`;
      const previous = lines.get(key);
      if (previous) previous.quantity++;
      else lines.set(key, { variant, quantity: 1, fit: { Band: choice.band, Cup: choice.cup } });
    }
    return [...lines.values()];
  }

  async submitFits(event) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (!this.canSubmit()) {
      this.querySelector('[data-status]').textContent = 'Choose a band and cup size for each item.';
      return;
    }
    this.busy = true; this.updateControls();
    const quantity = this.quantity;
    const groups = this.grouped(quantity);
    const lines = this.fitCartLines(quantity);
    const bundleId = crypto.randomUUID();
    const deferred = CartLinesUpdateEvent.createPromise(); deferred.promise.catch(() => {});
    this.formComponent.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: groups.map(({ variant, quantity }) => ({ merchandiseId: String(variant.id), quantity })), promise: deferred.promise }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean);
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: lines.map(({ variant, quantity: count, fit }) => ({ id: variant.id, quantity: count, properties: { _afz_bundle_id: bundleId, _afz_bundle_quantity: String(quantity), ...fit } })), sections: [...new Set(sectionIds)].join(','), sections_url: window.location.pathname }) });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || this.strings.cart_error);
      const discountResult = this.config.discounts.some(rule => rule.method === 'code') && window.AFZBundleDiscounts?.sync ? await window.AFZBundleDiscounts.sync({ resetProductId: this.dataset.productId }) : null;
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.strings.cart_error);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: this.formComponent.id || this.id, itemCount: quantity, productId: this.dataset.productId, sections: discountResult?.sections || added.sections, didError: false } });
      this.querySelector('[data-status]').textContent = this.strings.added;
    } catch (error) {
      deferred.reject(error); this.querySelector('[data-status]').textContent = error.message || this.strings.cart_error;
      this.formComponent.dispatchEvent(new CartErrorEvent({ error: error.message || this.strings.cart_error, code: 'INVALID' }));
    } finally { this.busy = false; this.updateControls(); }
  }
}

if (!customElements.get('afz-staylift-bundle')) customElements.define('afz-staylift-bundle', StayLiftBundle);
